import { Injectable, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { faro } from '@grafana/faro-web-sdk';
import { filter } from 'rxjs/operators';

/**
 * Service that monitors Angular router events and sends them to Faro.
 * Tracks page views, navigation timing, and navigation errors.
 */
@Injectable({ providedIn: 'root' })
export class FaroRouterService {
  private router = inject(Router);
  private navigationStartTime = 0;
  private previousUrl = '';

  /**
   * Initialize router monitoring.
   * Should be called once during app initialization.
   */
  initialize(): void {
    this.subscribeToRouterEvents();
  }

  private subscribeToRouterEvents(): void {
    // Track navigation start for timing
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        this.navigationStartTime = performance.now();
        faro.api?.pushEvent('navigation_start', {
          url: (event as NavigationStart).url,
          previousUrl: this.previousUrl,
        });
      });

    // Track successful navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;
        const duration = performance.now() - this.navigationStartTime;

        // Push page view event
        faro.api?.pushEvent('page_view', {
          url: navEvent.urlAfterRedirects,
          previousUrl: this.previousUrl,
        });

        // Push navigation timing measurement
        faro.api?.pushMeasurement({
          type: 'navigation_timing',
          values: { duration: Math.round(duration) },
        });

        this.previousUrl = navEvent.urlAfterRedirects;
      });

    // Track navigation errors
    this.router.events
      .pipe(filter((event) => event instanceof NavigationError))
      .subscribe((event) => {
        const navEvent = event as NavigationError;
        const error =
          navEvent.error instanceof Error
            ? navEvent.error
            : new Error(String(navEvent.error));

        faro.api?.pushError(error, {
          context: {
            type: 'navigation_error',
            url: navEvent.url,
          },
        });
      });

    // Track navigation cancellations
    this.router.events
      .pipe(filter((event) => event instanceof NavigationCancel))
      .subscribe((event) => {
        const navEvent = event as NavigationCancel;
        faro.api?.pushEvent('navigation_cancel', {
          url: navEvent.url,
          reason: String(navEvent.reason),
        });
      });
  }

  /**
   * Manually track a page view (useful for virtual page views or SPA navigation)
   */
  trackPageView(pageName: string, attributes?: Record<string, string>): void {
    faro.api?.pushEvent('page_view', {
      page: pageName,
      url: this.router.url,
      ...attributes,
    });
  }
}
